import type { FinancialActiveCarrier, FinancialCarrier } from '@/const/Financial'
import type { FinancialFilePreview } from '@/types/Components/FinancialFilePreview'
import type { FinancialCharge, FinancialChargeRole, FinancialImport, FinancialMatch } from '@/types/Models/Financial'
import { FINANCIAL_ADDITIONAL_PRODUCTS, FINANCIAL_DEUTSCHE_POST_HEADERS, FINANCIAL_DEUTSCHE_POST_LEGACY_ERROR } from '@/const/Financial'
import { financialDecimal, sumFinancialAmounts } from '@/services/FinancialAmounts'

const POSTNORD_SURCHARGES = ['Fuel surcharge', 'Sulphur surcharge', 'Currency surcharge', 'Credit surcharge']

// PostNord writes ordering dates as 20260813. Keep every charge date as YYYY-MM-DD for period selection.
export const financialIsoDate = (value: string): string => {
  const text = value.trim()
  const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(text)
  return compact ? `${compact[1]}-${compact[2]}-${compact[3]}` : text.slice(0, 10)
}

// Recognise the carrier from the columns. The earlier Deutsche Post report stays inspection-only.
export const financialImportCarrier = (source: FinancialFilePreview): FinancialActiveCarrier => {
  const carrier: FinancialCarrier | undefined = source.carrier
  if (!carrier)
    throw new Error('The columns do not match a CityMail, Earlybird, PostNord or Deutsche Post invoice. Upload the original carrier file.')
  if (carrier === 'Deutsche Post' && (source.worksheets ?? [source]).some((sheet) => sheet.headers.includes('Level')))
    throw new Error(FINANCIAL_DEUTSCHE_POST_LEGACY_ERROR)
  return carrier
}

export const normalizeFinancialImport = (
  source: FinancialFilePreview,
  carrier: FinancialActiveCarrier,
  fileName: string,
  fileHash: string,
  userId: string,
  userName: string,
): FinancialImport => {
  const charges: FinancialCharge[] = []
  let controlRows = 0
  let controlTotal: string | null = null
  let invoiceNumber = ''
  let currency = ''
  const sheets = source.worksheets ?? [{ ...source, name: 'Source', headerRow: 1 }]
  const additional = FINANCIAL_ADDITIONAL_PRODUCTS[carrier]
  for (const sheet of sheets) {
    if (carrier === 'Deutsche Post' && sheet.headers.includes('Level'))
      throw new Error(FINANCIAL_DEUTSCHE_POST_LEGACY_ERROR)
    const required =
      carrier === 'Deutsche Post'
        ? FINANCIAL_DEUTSCHE_POST_HEADERS
        : carrier === 'CityMail'
          ? ['PaketID', 'Paket pris', 'Datum']
          : carrier === 'PostNord'
            ? ['Item ID', 'Gross amount', 'Invoice number']
            : sheet.headers.includes('FeeTrackingId')
              ? ['FeeTrackingId', 'FeeAmount', 'FeeDefinitionName']
              : sheet.headers.includes('Product')
                ? ['Product', 'Sum']
                : ['TrackingId', 'Price', 'EventTime']
    if (!required.every((header) => sheet.headers.includes(header)))
      throw new Error(
        `The columns in ${sheet.name} do not match the ${carrier} invoice template. Upload the original carrier file.`,
      )
    if (sheet.rows.length !== sheet.rowCount) throw new Error('Read the complete file before importing.')
    if (carrier === 'Earlybird' && sheet.headers.includes('Product')) {
      // The control sheet repeats totals. Keep the invoice total for reconciliation, never as charges.
      controlRows += sheet.rowCount
      const product = sheet.headers.indexOf('Product')
      const sum = sheet.headers.indexOf('Sum')
      const total =
        sheet.rows.find((values) => values[product]?.trim().toUpperCase() === 'TOTAL') ??
        sheet.rows.find((values) => values[product]?.trim().toUpperCase() === 'SUM')
      if (total) controlTotal = financialDecimal(total[sum] ?? '')
      continue
    }
    sheet.rows.forEach((values, index) => {
      const row = Object.fromEntries(sheet.headers.map((header, column) => [header, values[column] ?? '']))
      const isFee = sheet.headers.includes('FeeTrackingId')
      const amountText =
        carrier === 'Deutsche Post'
          ? row['Net Price']
          : carrier === 'CityMail'
            ? row['Paket pris']
            : carrier === 'PostNord'
              ? row['Gross amount']
              : isFee
                ? row.FeeAmount
                : row.Price
      const components: Record<string, string> = {}
      if (carrier === 'PostNord') {
        // A missing Gross amount is invalid under every amount basis; absent surcharge cells mean no surcharge.
        components['Gross amount'] = financialDecimal(row['Gross amount'] ?? '') ?? 'invalid'
        for (const name of POSTNORD_SURCHARGES)
          components[name] = row[name]?.trim() ? (financialDecimal(row[name]) ?? 'invalid') : '0'
      }
      const invoice = (carrier === 'Deutsche Post' ? row['Invoice Number'] : row['Invoice number'])?.trim() ?? ''
      if (carrier === 'Deutsche Post') {
        const rowCurrency = row.Currency.trim().toUpperCase()
        if (!invoice || !/^[A-Z]{3}$/.test(rowCurrency))
          throw new Error('Every Deutsche Post row needs an invoice number and a three-letter currency. Check the original invoice.')
        if (currency && rowCurrency !== currency)
          throw new Error('This file contains more than one currency. Use one currency per import.')
        currency = rowCurrency
        if (row['Weight Unit'].trim() !== 'KGM')
          throw new Error('The Deutsche Post Weight column must use KGM. Check the original invoice; weights have not been converted.')
      }
      if (invoice && invoiceNumber && invoice !== invoiceNumber)
        throw new Error('This file contains more than one invoice number. Use one invoice per import.')
      if (invoice) invoiceNumber = invoice
      const product = (row.Produkttyp ?? row['Service name'] ?? row['Product Description'] ?? row.FeeDefinitionName ?? row.Size ?? '').normalize('NFC')
      charges.push({
        id: `${sheet.name}:${sheet.rowNumbers?.[index] ?? index + sheet.headerRow + 1}`,
        sheet: sheet.name,
        rowNumber: sheet.rowNumbers?.[index] ?? index + sheet.headerRow + 1,
        trackingNumber: (row.PaketID ?? row['Item ID'] ?? row['Shipment ID'] ?? row.FeeTrackingId ?? row.TrackingId ?? '').trim(),
        reference: row.Referens ?? row.Reference ?? row['Shippers Reference 1'] ?? '',
        date: financialIsoDate(row.Datum ?? row['Ordering date'] ?? row['Service Date'] ?? row.FeeCreated ?? row.EventTime ?? ''),
        description: product || 'Delivery',
        kind: isFee || additional?.test(product) ? 'surcharge' : 'charge',
        amount: financialDecimal(amountText ?? ''),
        weight: row.Vikt ?? row['Gross weight'] ?? row.Weight ?? '',
        source: row,
        components,
        matchStatus: 'unmatched',
        match: null,
        review: null,
      })
    })
  }
  if (!charges.length) throw new Error('The file contains no charge rows.')
  return {
    revision: 0,
    id: crypto.randomUUID(),
    carrier,
    fileName,
    fileHash,
    invoiceNumber,
    currency,
    amountBasis: '',
    invoiceTotal: '',
    controlTotal,
    createdAt: new Date().toISOString(),
    createdById: userId,
    createdByName: userName,
    matchedAt: null,
    charges,
    controlRows,
    sourceRows: charges.length + controlRows,
    exports: [],
  }
}

export const financialChargeAmount = (batch: FinancialImport, row: FinancialCharge): string | null => {
  if (batch.carrier !== 'PostNord' || batch.amountBasis !== 'gross-plus-surcharges') return row.amount
  const amounts = Object.values(row.components)
  return row.amount === null || amounts.some((value) => financialDecimal(value) === null)
    ? null
    : sumFinancialAmounts(amounts)
}

// Derived at read time so drafts saved before a carrier product was classified still show the right role.
export const financialRole = (batch: FinancialImport, row: FinancialCharge): FinancialChargeRole => {
  if (row.amount !== null && Number(row.amount) < 0) return 'credit'
  if (row.kind === 'surcharge') return 'additional'
  return FINANCIAL_ADDITIONAL_PRODUCTS[batch.carrier]?.test(row.description.normalize('NFC')) ? 'additional' : 'base'
}

export const financialMetadataComplete = (batch: FinancialImport): boolean => {
  return (
    !!batch.invoiceNumber.trim() &&
    /^[A-Z]{3}$/.test(batch.currency) &&
    (batch.carrier !== 'PostNord' || !!batch.amountBasis)
  )
}

export const financialMissingMetadata = (batch: FinancialImport): string[] => {
  return [
    !batch.invoiceNumber.trim() ? 'invoice number' : '',
    !/^[A-Z]{3}$/.test(batch.currency) ? 'invoice currency' : '',
    batch.carrier === 'PostNord' && !batch.amountBasis ? 'surcharge amount basis' : '',
  ].filter(Boolean)
}

export const financialReady = (batch: FinancialImport, row: FinancialCharge): boolean => {
  return (
    financialMetadataComplete(batch) &&
    row.matchStatus === 'matched' &&
    !!row.match &&
    row.review?.status === 'approved' &&
    row.review.customerPrice !== null &&
    financialDecimal(row.review.customerPrice) !== null &&
    financialChargeAmount(batch, row) !== null
  )
}

// The customer an invoice row is billed to: the company, or one of its sender addresses.
export const financialBillingId = (match: FinancialMatch | null | undefined): string => {
  return match ? (match.billingId ?? match.companyId) : ''
}

export const financialBillingName = (match: FinancialMatch | null | undefined): string => {
  return match ? (match.billingName ?? match.companyName) : 'Unassigned'
}

// Calendar date in Swedish local time for API timestamps; plain dates are returned unchanged.
export const financialLocalDate = (value: string): string => {
  if (!value) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value.slice(0, 10) : date.toLocaleDateString('sv-SE', { timeZone: 'Europe/Stockholm' })
}

export const financialInvoiceLabel = (batch: FinancialImport): string => {
  return batch.invoiceNumber || batch.fileName
}

export const financialSameInvoiceNumber = (left: string, right: string): boolean => {
  const normalize = (value: string): string => value.replace(/\s+/g, '').toUpperCase()
  return !!normalize(left) && normalize(left) === normalize(right)
}
